import React, {Fragment, useCallback, useContext, useEffect, useState} from "react";
import {Button, Card, Col, Form, Row, Spinner, Table} from "react-bootstrap";
import {
    HealthConditionMaster,
    getHealthConditionMasters,
} from "../../api-service/health-condition-master";
import {PaginationComponent} from "../../parts/pagination-component";
import {HealthConditionFormModal} from "./health-condition-form-modal";
import {TsAdminContext} from "../../context/ts-admin";
import {toast} from "react-toastify";
import {getApiErrorMessage} from "../../common";

const PER_PAGE = 20;

const columns = [
    {dataField: "Code", width: "30%", text: "コード"},
    {dataField: "Name", width: "58%", text: "名称"},
];

export function HealthConditionMasterPage() {
    const [orgInfo] = useContext(TsAdminContext);
    const [isLoading, setIsLoading] = useState(false);
    const [searchData, setSearchData] = useState({
        code: "",
        name: "",
    });

    const [currentItems, setCurrentItems] = useState<HealthConditionMaster[]>([]);
    const [paginatedItems, setPaginatedItems] = useState<HealthConditionMaster[]>([]);
    const [totalRecord, setTotalRecord] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const currentPageOffset = (pageNumber * PER_PAGE) - PER_PAGE;

    const [showFormModal, setShowFormModal] = useState(false);
    const [formModalMode, setFormModalMode] = useState<"create" | "edit">("create");
    const [selectedRecord, setSelectedRecord] = useState<HealthConditionMaster | null>(null);

    const loadList = useCallback(async () => {
        setIsLoading(true);
        const targetOrg = localStorage.getItem('target-org') || orgInfo?.currentOrg?.OrgId;
        try {
            const items = await getHealthConditionMasters({
                Code: searchData.code,
                Name: searchData.name,
            }, targetOrg);

            setCurrentItems(items);
            setPaginatedItems(items.slice(0, PER_PAGE));
            setTotalRecord(items.length);
            setPageNumber(1);
        } catch (error: any) {
            toast.error(getApiErrorMessage(error, "データ取得に失敗しました。再度お試しください。"));
        } finally {
            setIsLoading(false);
        }
    }, [searchData, orgInfo]);

    useEffect(() => {
        loadList();
    }, []);

    useEffect(() => {
        const tmpItems = [...currentItems];
        setPaginatedItems(tmpItems.splice(currentPageOffset, PER_PAGE));
    }, [pageNumber, currentItems, currentPageOffset]);

    const setPage = (type: string) => {
        switch (type) {
            case "start-page":
                setPageNumber(1);
                break;
            case "next-page":
                setPageNumber(pageNumber + 1);
                break;
            case "prev-page":
                if (pageNumber === 1) return;
                setPageNumber(pageNumber - 1);
                break;
            case "end-page":
                setPageNumber(Math.ceil(totalRecord / PER_PAGE));
                break;
        }
    };

    const handleOpenCreateModal = () => {
        setFormModalMode("create");
        setSelectedRecord(null);
        setShowFormModal(true);
    };

    const handleOpenEditModal = (item: HealthConditionMaster) => {
        setFormModalMode("edit");
        setSelectedRecord(item);
        setShowFormModal(true);
    };

    const handleCloseFormModal = () => {
        setShowFormModal(false);
    };

    const handleFormModalSaved = () => {
        setShowFormModal(false);
        loadList();
    };

    const handleFormModalDeleted = () => {
        setShowFormModal(false);
        loadList();
    };

    return (
        <Fragment>
            <div className="health-condition-master">
                <div className="mt-3">
                    <Card className="mt-3 card-container">
                        <Card.Body className="page-title">データ送信マスタ</Card.Body>
                    </Card>
                    <Form>
                        <Row className="d-flex align-items-center p-3 pb-2">
                            <Col xs={12} xl="auto" className="d-flex align-items-center col-5 pt-2">
                                <Form.Label className="mt-2 me-2 form-search-label">
                                    コード
                                </Form.Label>
                                <Form.Control
                                    className="station-form-control"
                                    onChange={(e) => setSearchData({...searchData, code: e.target.value})}
                                />
                            </Col>
                            <Col xs={12} xl="auto" className="d-flex align-items-center col-5 pt-2">
                                <Form.Label className="mt-2 me-2 form-search-label">
                                    名称
                                </Form.Label>
                                <Form.Control
                                    className="station-form-control"
                                    onChange={(e) => setSearchData({...searchData, name: e.target.value})}
                                />
                            </Col>
                            <Col xs={4} md="auto" lg="auto" className="align-items-center pt-2">
                                {isLoading ? (
                                    <Button size="sm" className="button-search" disabled>
                                        <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true"/>
                                        検索...
                                    </Button>
                                ) : (
                                    <Button size="sm" className="button-search" onClick={() => loadList()}>
                                        検索
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </Form>
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <Button size="sm" className="me-2 btn-save" onClick={handleOpenCreateModal}>
                                <svg className="mx-1" width="14" height="14" viewBox="0 0 14 14" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path fill-rule="evenodd" clip-rule="evenodd"
                                          d="M8 7.69231H13.0769C13.3231 7.69231 13.5385 7.47692 13.5385 7.23077V6.30769C13.5385 6.06154 13.3231 5.84615 13.0769 5.84615H8C7.81538 5.84615 7.69231 5.72308 7.69231 5.53846V0.461538C7.69231 0.215385 7.47692 0 7.23077 0H6.30769C6.06154 0 5.84615 0.215385 5.84615 0.461538V5.53846C5.84615 5.72308 5.72308 5.84615 5.53846 5.84615H0.461538C0.215385 5.84615 0 6.06154 0 6.30769V7.23077C0 7.47692 0.215385 7.69231 0.461538 7.69231H5.53846C5.72308 7.69231 5.84615 7.81538 5.84615 8V13.0769C5.84615 13.3231 6.06154 13.5385 6.30769 13.5385H7.23077C7.47692 13.5385 7.69231 13.3231 7.69231 13.0769V8C7.69231 7.81538 7.81538 7.69231 8 7.69231Z"
                                          fill="white"/>
                                </svg>

                                新規追加
                            </Button>
                        </div>
                        <div className="d-flex align-items-center">
                            {totalRecord > 0 && (
                                <p className="fs-13 mb-0">{currentPageOffset + paginatedItems.length}/{totalRecord}件</p>
                            )}
                        </div>
                    </div>
                    {paginatedItems.length > 0 ? (
                        <Table className="mt-3 rounded-0 table-health-condition">
                            <thead>
                            <tr>
                                {columns.map((col, index) => (
                                    <th key={index} style={{width: col.width}}>
                                        {col.text}
                                    </th>
                                ))}
                                <th style={{width: "12%"}}></th>
                            </tr>
                            </thead>
                            <tbody>
                            {
                                paginatedItems.map((item, key) => (
                                    <tr key={key}>
                                        <td>
                                            <div className="cell-wrap-2">{item.Code}</div>
                                        </td>
                                        <td>
                                            <div className="cell-wrap-2">{item.Name}</div>
                                        </td>
                                        <td>
                                            <Button
                                                variant="light"
                                                className="btn-dept-action"
                                                size="sm"
                                                onClick={() => handleOpenEditModal(item)}
                                            >
                                                編集
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            }
                            </tbody>
                        </Table>
                    ) : (
                        <h6 className="text-center pt-5 fw-bold">
                            {!isLoading ? "データがありません。" : "検索中・・・・"}
                        </h6>
                    )}
                    {currentItems.length > 0 && (
                        <PaginationComponent
                            totalPage={Math.ceil(totalRecord / PER_PAGE)}
                            handlePage={setPage}
                            currentPage={pageNumber}
                        />
                    )}
                </div>
            </div>
            <HealthConditionFormModal
                show={showFormModal}
                mode={formModalMode}
                initialData={selectedRecord}
                onClose={handleCloseFormModal}
                onSaved={handleFormModalSaved}
                onDeleted={handleFormModalDeleted}
            />
        </Fragment>
    );
}
